import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Save, FileDown, Printer, Mail, Plus, Trash2, ClipboardCheck } from 'lucide-react';
import { SignaturePad } from '../components/SignaturePad';
import { parseApiError } from '../lib/parseApiError';
import { uid } from '../lib/uid';
import { TIPI_INTERVENTO, CHECKLIST_VP, NATURE_IVA } from '../lib/rapportinoConst';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VUOTO = {
  cliente_id: '', impianto_id: null, marca_rt: '', modello_rt: '', matricola_rt: '', firmware_rt: '',
  tipi_intervento: [], descrizione_intervento: '', luogo: 'Presso il cliente', commerciale: 'A pagamento',
  scadenza_abbonamento: null, reparti: [], diritto_chiamata: 0, ore_lavoro: 0, costo_ore: 0,
  ricambi: '', costo_ricambi: 0, totale: 0, modalita_pagamento: null, riba_banca: '', checklist: {}, sezione3_na: false, esito_vp: null,
  scadenza_vp: '', note: '', ora_inizio: '', ora_fine: '', firma_cliente_b64: null, firma_cliente_nome: '',
};

export default function RapportinoPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ ...VUOTO, cliente_id: params.get('cliente_id') || '' });
  const [clienteNome, setClienteNome] = useState('');
  const [savedId, setSavedId] = useState(id || null);
  const [saving, setSaving] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailExtra, setEmailExtra] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const init = async () => {
      if (id) {
        try {
          const { data } = await axios.get(`${API}/rapportini/${id}`);
          setForm({ ...VUOTO, ...data, reparti: (data.reparti || []).map((r) => ({ ...r, _uid: uid() })) });
          setClienteNome(data.cliente_nome || '');
        } catch { toast.error('Rapportino non trovato'); }
        return;
      }
      const cid = params.get('cliente_id');
      const iid = params.get('impianto_id');
      if (cid) {
        try {
          const { data: cli } = await axios.get(`${API}/clienti/${cid}`);
          setClienteNome(cli.ragione_sociale || '');
        } catch (e) { console.error('Prefill cliente fallito:', e); }
      }
      if (iid && cid) {
        try {
          const { data: imps } = await axios.get(`${API}/impianti?cliente_id=${cid}`);
          const imp = imps.find((i) => i.id === iid);
          if (imp) setForm((f) => ({ ...f, impianto_id: iid, marca_rt: imp.marca || '', modello_rt: imp.modello || '', matricola_rt: imp.matricola || '' }));
        } catch (e) { console.error('Prefill impianto fallito:', e); }
      }
    };
    init();
  }, [id, params]);

  // Totale automatico
  useEffect(() => {
    const tot = (parseFloat(form.diritto_chiamata) || 0) + (parseFloat(form.ore_lavoro) || 0) * (parseFloat(form.costo_ore) || 0) + (parseFloat(form.costo_ricambi) || 0);
    setForm((f) => (f.totale === tot ? f : { ...f, totale: Math.round(tot * 100) / 100 }));
  }, [form.diritto_chiamata, form.ore_lavoro, form.costo_ore, form.costo_ricambi]);

  const salva = async () => {
    if (!form.cliente_id) { toast.error('Cliente mancante'); return null; }
    setSaving(true);
    try {
      const payload = { ...form, scadenza_vp: form.scadenza_vp || null, scadenza_abbonamento: form.scadenza_abbonamento || null };
      let rid = savedId;
      if (rid) {
        await axios.put(`${API}/rapportini/${rid}`, payload);
      } else {
        const { data } = await axios.post(`${API}/rapportini`, payload);
        rid = data.id;
        setSavedId(rid);
      }
      toast.success('✅ Rapportino salvato');
      return rid;
    } catch (e) { toast.error(parseApiError(e, 'Errore salvataggio')); return null; }
    finally { setSaving(false); }
  };

  const scaricaPdf = async (blank = false, stampa = false) => {
    const rid = await salva();
    if (!rid) return;
    try {
      const res = await axios.get(`${API}/rapportini/${rid}/pdf?blank=${blank}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (stampa) { const w = window.open(url); if (w) w.onload = () => w.print(); }
      else {
        const a = document.createElement('a');
        a.href = url; a.download = `rapportino${blank ? '_bianco' : ''}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
    } catch (e) { toast.error(parseApiError(e, 'Errore PDF')); }
  };

  const inviaEmail = async () => {
    const rid = await salva();
    if (!rid) return;
    setSendingEmail(true);
    try {
      const { data } = await axios.post(`${API}/rapportini/${rid}/invia-email`, { email_extra: emailExtra || null });
      toast.success(`📧 Rapportino inviato a: ${data.destinatari.join(', ')}`);
      setEmailDialogOpen(false);
      setEmailExtra('');
    } catch (e) { toast.error(parseApiError(e, 'Errore invio email'), { duration: 8000 }); }
    finally { setSendingEmail(false); }
  };

  const setCheck = (cod, val) => setForm((f) => ({ ...f, checklist: { ...f.checklist, [cod]: f.checklist[cod] === val ? undefined : val } }));
  const setReparto = (i, k, v) => setForm((f) => { const rep = [...f.reparti]; rep[i] = { ...rep[i], [k]: v }; return { ...f, reparti: rep }; });

  const Sez = ({ t }) => <h2 className="text-sm font-mono uppercase tracking-wider text-cyan-600 dark:text-cyan-400 mt-6 mb-2">{t}</h2>;

  return (
    <div className="max-w-4xl mx-auto pb-24 px-4">
      <div className="flex items-center gap-3 py-4">
        <Button variant="ghost" size="icon" className="rounded-none" onClick={() => navigate(-1)} data-testid="rapportino-back"><ArrowLeft className="h-5 w-5" /></Button>
        <div>
          <h1 className="text-xl font-black uppercase flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-cyan-500" /> Rapporto di Intervento RT</h1>
          <p className="text-xs text-muted-foreground">{clienteNome || 'Cliente'} {savedId ? '· salvato' : '· nuovo'}</p>
        </div>
      </div>

      <Sez t="Registratore Telematico" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[['marca_rt', 'Marca'], ['modello_rt', 'Modello'], ['matricola_rt', 'Matricola'], ['firmware_rt', 'Firmware']].map(([k, l]) => (
          <div key={k}><Label className="text-xs font-mono uppercase">{l}</Label>
            <Input value={form[k] || ''} onChange={(e) => set(k, e.target.value)} className="rounded-none" data-testid={`rap-${k}`} /></div>
        ))}
      </div>

      <Sez t="Intervento richiesto" />
      <div className="grid md:grid-cols-2 gap-1">
        {TIPI_INTERVENTO.map((t) => (
          <label key={t} className="flex items-center gap-2 text-sm p-1.5 border border-transparent hover:border-border cursor-pointer">
            <Checkbox checked={form.tipi_intervento.includes(t)} onCheckedChange={(v) => set('tipi_intervento', v ? [...form.tipi_intervento, t] : form.tipi_intervento.filter((x) => x !== t))} data-testid={`rap-tipo-${t.substring(0, 10)}`} />
            {t}
          </label>
        ))}
      </div>
      <Textarea value={form.descrizione_intervento || ''} onChange={(e) => set('descrizione_intervento', e.target.value)} placeholder="Descrizione intervento (opzionale)" className="rounded-none mt-2" data-testid="rap-descrizione" />

      <div className="grid md:grid-cols-2 gap-4 mt-3">
        <div><Label className="text-xs font-mono uppercase">Luogo</Label>
          <div className="flex gap-1 mt-1">{['Presso il cliente', 'Laboratorio', 'Da remoto'].map((l) => (
            <Button key={l} type="button" size="sm" variant={form.luogo === l ? 'default' : 'outline'} className="rounded-none text-xs" onClick={() => set('luogo', l)}>{l}</Button>))}</div></div>
        <div><Label className="text-xs font-mono uppercase">Commerciale</Label>
          <div className="flex gap-1 mt-1">{['A pagamento', 'In garanzia', 'Cliente in abbonamento'].map((l) => (
            <Button key={l} type="button" size="sm" variant={form.commerciale === l ? 'default' : 'outline'} className="rounded-none text-xs" onClick={() => set('commerciale', l)}>{l}</Button>))}</div></div>
      </div>

      <Sez t="Programmazione reparti IVA" />
      {form.reparti.map((rep, i) => (
        <div key={rep._uid || `rep-${i}`} className="grid grid-cols-6 gap-1 mb-1 items-center">
          <Input placeholder="N." value={rep.numero || ''} onChange={(e) => setReparto(i, 'numero', e.target.value)} className="rounded-none text-xs" />
          <Input placeholder="Descrizione" value={rep.descrizione || ''} onChange={(e) => setReparto(i, 'descrizione', e.target.value)} className="rounded-none text-xs col-span-2" />
          <select value={rep.natura_iva || '-'} onChange={(e) => setReparto(i, 'natura_iva', e.target.value)} className="border border-input h-9 text-xs px-1 bg-background">{NATURE_IVA.map((n) => <option key={n}>{n}</option>)}</select>
          <Input placeholder="Aliq. %" value={rep.aliquota || ''} onChange={(e) => setReparto(i, 'aliquota', e.target.value)} className="rounded-none text-xs" />
          <div className="flex gap-1">
            <Input placeholder="ATECO" value={rep.codice_ateco || ''} onChange={(e) => setReparto(i, 'codice_ateco', e.target.value)} className="rounded-none text-xs" />
            <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => set('reparti', form.reparti.filter((_, j) => j !== i))}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" className="rounded-none" onClick={() => set('reparti', [...form.reparti, { numero: String(form.reparti.length + 1), bene_servizio: 'Bene', _uid: uid() }])} data-testid="rap-add-reparto"><Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi reparto</Button>

      <Sez t="Costi" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[['diritto_chiamata', 'Diritto chiamata €'], ['ore_lavoro', 'Ore'], ['costo_ore', '€/ora'], ['costo_ricambi', 'Ricambi €']].map(([k, l]) => (
          <div key={k}><Label className="text-xs font-mono uppercase">{l}</Label>
            <Input type="number" step="0.5" value={form[k]} onChange={(e) => set(k, e.target.value)} className="rounded-none" data-testid={`rap-${k}`} /></div>
        ))}
      </div>
      <div className="flex flex-wrap gap-6 mt-2 items-end border border-border p-3 bg-muted/30">
        <div><Label className="text-xs font-mono uppercase">Imponibile €</Label>
          <div className="font-mono font-semibold" data-testid="rap-totale">{form.totale.toFixed(2)}</div></div>
        <div><Label className="text-xs font-mono uppercase">IVA 22% €</Label>
          <div className="font-mono font-semibold" data-testid="rap-iva">{(form.totale * 0.22).toFixed(2)}</div></div>
        <div><Label className="text-xs font-mono uppercase">Totale €</Label>
          <div className="font-mono font-black text-xl text-cyan-600 dark:text-cyan-400" data-testid="rap-totale-ivato">{(form.totale * 1.22).toFixed(2)}</div></div>
      </div>

      <Sez t="Modalità di pagamento" />
      <div className="flex flex-wrap gap-1">
        {['Contanti', 'Bonifico', 'RiBa'].map((m) => (
          <Button key={m} type="button" variant={form.modalita_pagamento === m ? 'default' : 'outline'} className="rounded-none" onClick={() => set('modalita_pagamento', form.modalita_pagamento === m ? null : m)} data-testid={`rap-pag-${m}`}>{m}</Button>
        ))}
      </div>
      {form.modalita_pagamento === 'Bonifico' && (
        <p className="text-xs text-muted-foreground mt-2">Sul PDF verrà stampato l'IBAN di Velta Sistemi (da Impostazioni).</p>
      )}
      {form.modalita_pagamento === 'RiBa' && (
        <div className="max-w-sm mt-2"><Label className="text-xs font-mono uppercase">Banca del cliente</Label>
          <Input value={form.riba_banca || ''} onChange={(e) => set('riba_banca', e.target.value)} placeholder="es. Intesa Sanpaolo - Filiale di Salò" className="rounded-none" data-testid="rap-riba-banca" /></div>
      )}

      <div className="grid grid-cols-2 gap-2 mt-3 max-w-xs">
        <div><Label className="text-xs font-mono uppercase">Inizio</Label><Input type="time" value={form.ora_inizio || ''} onChange={(e) => set('ora_inizio', e.target.value)} className="rounded-none" /></div>
        <div><Label className="text-xs font-mono uppercase">Fine</Label><Input type="time" value={form.ora_fine || ''} onChange={(e) => set('ora_fine', e.target.value)} className="rounded-none" /></div>
      </div>

      <Sez t="Verificazione Periodica RT (Allegato VIII 6.1 f)" />
      {Object.entries(CHECKLIST_VP).map(([sez, voci]) => (
        <div key={sez} className="mb-3">
          <div className="flex items-center justify-between bg-muted px-2 py-1.5">
            <span className="text-xs font-bold uppercase">{sez}</span>
            {sez.startsWith('3.') && (
              <label className="flex items-center gap-1 text-xs"><Checkbox checked={form.sezione3_na} onCheckedChange={(v) => set('sezione3_na', !!v)} /> Non applicabile</label>
            )}
          </div>
          {(!sez.startsWith('3.') || !form.sezione3_na) && voci.map(([cod, testo]) => (
            <div key={cod} className="flex items-center justify-between gap-2 py-1 border-b border-border/50 text-sm">
              <span className="flex-1"><span className="font-mono text-xs text-muted-foreground mr-2">{cod}</span>{testo}</span>
              <div className="flex gap-1 shrink-0">
                <Button type="button" size="sm" variant={form.checklist[cod] === 'SI' ? 'default' : 'outline'} className="rounded-none h-7 px-3 text-xs" onClick={() => setCheck(cod, 'SI')} data-testid={`rap-check-${cod}-si`}>SI</Button>
                <Button type="button" size="sm" variant={form.checklist[cod] === 'NO' ? 'destructive' : 'outline'} className="rounded-none h-7 px-3 text-xs" onClick={() => setCheck(cod, 'NO')} data-testid={`rap-check-${cod}-no`}>NO</Button>
              </div>
            </div>
          ))}
        </div>
      ))}

      <div className="grid md:grid-cols-3 gap-3 items-end">
        <div><Label className="text-xs font-mono uppercase">Esito Verificazione</Label>
          <div className="flex gap-1 mt-1">
            <Button type="button" variant={form.esito_vp === 'POSITIVO' ? 'default' : 'outline'} className="rounded-none" onClick={() => set('esito_vp', 'POSITIVO')} data-testid="rap-esito-pos">✓ POSITIVO</Button>
            <Button type="button" variant={form.esito_vp === 'NEGATIVO' ? 'destructive' : 'outline'} className="rounded-none" onClick={() => set('esito_vp', 'NEGATIVO')} data-testid="rap-esito-neg">✗ NEGATIVO</Button>
          </div></div>
        <div><Label className="text-xs font-mono uppercase">Scadenza prossima VP</Label>
          <Input type="date" value={form.scadenza_vp || ''} onChange={(e) => set('scadenza_vp', e.target.value)} className="rounded-none" data-testid="rap-scadenza-vp" /></div>
        <div><Label className="text-xs font-mono uppercase">Note</Label>
          <Input value={form.note || ''} onChange={(e) => set('note', e.target.value)} className="rounded-none" data-testid="rap-note" /></div>
      </div>

      <Sez t="Firma del cliente" />
      <SignaturePad value={form.firma_cliente_b64} onChange={(d) => set('firma_cliente_b64', d)} />

      {/* Barra azioni fissa */}
      <div className="fixed bottom-0 left-0 right-0 bg-background border-t border-border p-3 flex flex-wrap gap-2 justify-center z-40">
        <Button onClick={salva} disabled={saving} className="rounded-none" data-testid="rap-salva-btn"><Save className="h-4 w-4 mr-2" />{saving ? 'Salvo...' : 'Salva'}</Button>
        <Button variant="outline" onClick={() => scaricaPdf(false)} className="rounded-none" data-testid="rap-pdf-btn"><FileDown className="h-4 w-4 mr-2" />PDF</Button>
        <Button variant="outline" onClick={() => scaricaPdf(true)} className="rounded-none" data-testid="rap-pdf-blank-btn"><Printer className="h-4 w-4 mr-2" />Stampa in bianco</Button>
        <Button variant="outline" onClick={() => setEmailDialogOpen(true)} className="rounded-none" data-testid="rap-email-btn"><Mail className="h-4 w-4 mr-2" />Invia al cliente</Button>
      </div>

      {/* Dialog invio email */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-md rounded-none">
          <DialogHeader><DialogTitle className="font-mono uppercase">Invia rapportino via email</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Il rapportino verrà inviato all'email salvata nel CRM. Puoi aggiungere un'altra email indicata dal cliente al momento.</p>
          <div><Label className="text-xs font-mono uppercase">Email aggiuntiva (opzionale)</Label>
            <Input type="email" value={emailExtra} onChange={(e) => setEmailExtra(e.target.value)} placeholder="es. mario.rossi@gmail.com" className="rounded-none" data-testid="rap-email-extra" /></div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => setEmailDialogOpen(false)}>Annulla</Button>
            <Button className="rounded-none" onClick={inviaEmail} disabled={sendingEmail} data-testid="rap-email-send-btn">
              <Mail className="h-4 w-4 mr-2" />{sendingEmail ? 'Invio...' : 'Invia'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
